import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DatePicker } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { RepeatIcon } from "lucide-react";
import { notify } from "@/components/common/Notification";
import { PtsExpenseSummaryService } from "@/services/APIs";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";

interface RecurringExpenseModalProps {
  open: boolean;
  onClose: () => void;
  defaultYear?: number;
}

const RecurringExpenseModal = ({
  open,
  onClose,
  defaultYear,
}: RecurringExpenseModalProps) => {
  const queryClient = useQueryClient();
  const [selectedYear, setSelectedYear] = useState<Dayjs>(
    dayjs().year(defaultYear ?? new Date().getFullYear())
  );

  const year = selectedYear.year();

  const recurringMutation = useMutation({
    mutationFn: () => {
      return PtsExpenseSummaryService.createRecurringExpenses(year);
    },
    onSuccess: result => {
      // axiosUtils.get() catches errors and returns undefined on failure,
      // so onSuccess fires even when the API fails. Guard against that here.
      if (!result) {
        return;
      }
      notify.success("Recurring expenses created successfully for " + year);
      queryClient.invalidateQueries({ queryKey: ["expense-summary"] });
      onClose();
    },
    onError: () => {
      // Error toast is handled automatically by the axios interceptor (axiosConfig.ts)
    },
  });

  const handleSubmit = () => {
    if (!selectedYear) {
      notify.error("Please select a year");
      return;
    }
    recurringMutation.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-sm flex-col bg-white p-1 sm:w-full">
        <DialogHeader className="flex-shrink-0 px-6 pb-6 pt-6">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50">
              <RepeatIcon className="h-4 w-4 text-indigo-600" />
            </span>
            <div>
              <DialogTitle>Create Recurring Expenses</DialogTitle>
              <DialogDescription className="mt-0.5 text-xs">
                Generate recurring expenses for the selected year.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="recurring-year">Year</Label>
            <DatePicker
              id="recurring-year"
              picker="year"
              value={selectedYear}
              onChange={date => {
                if (date) {
                  setSelectedYear(date);
                }
              }}
              allowClear={false}
              className="h-10 w-full [&_.ant-picker-input>input]:!text-sm [&_.ant-picker]:!rounded-md"
              getPopupContainer={trigger =>
                trigger.parentElement ?? document.body
              }
            />
            <p className="text-xs text-gray-400">
              All active recurring expenses will be generated for this year.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 flex-col gap-2 border-t px-6 pb-6 pt-4 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={recurringMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={recurringMutation.isPending}
          >
            {recurringMutation.isPending ? "Creating..." : "Create Recurring"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecurringExpenseModal;
